import { useNavigate } from 'react-router-dom'
import { Plus, Eye, Mail } from 'lucide-react'
import { NodeAvatar } from '@/components/nodes/NodeAvatar'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { UserHoverCard } from '@/components/users/UserHoverCard'
import { User } from '@/types'
import { LucideIcon } from 'lucide-react'

interface ReadStatus {
  /** Whether this user has read the message */
  hasRead: boolean
}

interface NodeUsersProps {
  /** Array of users to display */
  users: User[]
  /** Maximum number of users to show before "+X more" indicator */
  maxVisible?: number
  /** URL to navigate to when "View all" is clicked */
  viewAllUrl?: string
  /** Label for the "View all" button */
  viewAllLabel?: string
  /** Whether to show the "View all" button */
  showViewAll?: boolean
  /** Size of the avatars (sm, md, lg) */
  avatarSize?: 'sm' | 'md' | 'lg'
  /** Whether to show user roles in tooltips */
  showRole?: boolean
  /** Additional CSS classes for the container */
  className?: string
  /** Custom empty state message when no users */
  emptyState?: React.ReactNode
  /** Icon component to display before the user list */
  icon?: LucideIcon
  /** Label text to display before the user list */
  label?: string
  /** Optional read status for each user (for message recipients) */
  readStatuses?: ReadStatus[]
  /** Whether to show read/unread badges */
  showReadStatus?: boolean
  /** Whether to stack the label above the users list */
  labelStacked?: boolean
  /** Whether to show user names next to avatars */
  showUserName?: boolean
}

/**
 * NodeUsers Component
 *
 * Displays a list of users associated with any node (workspace, project, task, etc.)
 *
 * @example
 * ```tsx
 * // Basic usage for a workspace
 * <NodeUsers
 *   users={workspace.users}
 *   maxVisible={5}
 *   viewAllUrl={`/workspaces/${workspace.id}/members`}
 *   viewAllLabel="View all members"
 * />
 *
 * // For a project with smaller avatars
 * <NodeUsers
 *   users={project.users}
 *   avatarSize="sm"
 *   showViewAll={false}
 * />
 *
 * // With label and icon
 * <NodeUsers
 *   users={project.users}
 *   maxVisible={3}
 *   avatarSize="sm"
 *   showViewAll={false}
 *   icon={Users}
 *   label="Team:"
 * />
 *
 * // For message recipients with read status
 * <NodeUsers
 *   users={message.users.filter(u => u.id !== message.creator.id)}
 *   maxVisible={10}
 *   avatarSize="sm"
 *   showReadStatus={true}
 *   readStatuses={message.users
 *     .filter(u => u.id !== message.creator.id)
 *     .map(recipient => ({
 *       hasRead: message.readBy.some(r => r.userId === recipient.id)
 *     }))
 *   }
 * />
 *
 * // Stacked label display
 * <NodeUsers
 *   users={project.users}
 *   label="Team:"
 *   labelStacked={true}
 *   maxVisible={5}
 * />
 *
 * // With user names displayed
 * <NodeUsers
 *   users={task.users}
 *   showUserName={true}
 *   maxVisible={5}
 *   avatarSize="sm"
 * />
 * ```
 */
export function NodeUsers({
  users,
  maxVisible = 5,
  viewAllUrl,
  viewAllLabel = 'View all users',
  showViewAll = true,
  avatarSize = 'md',
  showRole = false,
  className = '',
  emptyState,
  icon: Icon,
  label = '',
  readStatuses,
  showReadStatus = false,
  labelStacked = false,
  showUserName = false,
}: NodeUsersProps) {
  const navigate = useNavigate()
  const visibleUsers = users.slice(0, maxVisible)
  const hiddenCount = users.length - maxVisible

  const handleUserClick = (userId: string) => {
    navigate(`/users/${userId}`)
  }

  if (users.length === 0) {
    return (
      <div
        className={`flex flex-col sm:${labelStacked ? 'flex-col items-start' : 'flex-row sm:items-center'} gap-2 ${className}`}
      >
        {label && (
          <div className="flex items-center gap-1">
            {Icon && <Icon className="h-4 w-4" />}
            <span className="text-sm text-muted-foreground">{label}</span>
          </div>
        )}
        {emptyState || (
          <div className="text-sm text-muted-foreground">No users associated with this node</div>
        )}
      </div>
    )
  }

  return (
    <TooltipProvider>
      <div
        className={`flex flex-col sm:${labelStacked ? 'flex-col items-start' : 'flex-row sm:items-center'} gap-2 ${className}`}
      >
        {label && (
          <div className="flex items-center gap-1">
            {Icon && <Icon className="h-4 w-4" />}
            <span className="text-sm text-muted-foreground">{label}</span>
          </div>
        )}
        <div className="flex items-center flex-wrap gap-1">
          {visibleUsers
            .filter((user) => user != null)
            .map((user, index) => {
              const readStatus = readStatuses?.[index]
              const hasRead = readStatus?.hasRead

              return (
                <div key={user.id} className="flex items-center gap-1">
                  <UserHoverCard user={user}>
                    <button
                      onClick={() => handleUserClick(user.id)}
                      className={`transition-transform hover:scale-110 hover:z-10 relative border-2 border-background rounded-full cursor-pointer`}
                      style={{ zIndex: users.length - index }}
                    >
                      <NodeAvatar
                        node={user}
                        size={avatarSize as 'sm' | 'md' | 'lg'}
                        className={showRole ? 'ring-2 ring-primary/20' : ''}
                      />
                    </button>
                  </UserHoverCard>
                  {showUserName && (
                    <button
                      onClick={() => handleUserClick(user.id)}
                      className="text-sm font-medium hover:underline cursor-pointer bg-transparent border-none p-0"
                    >
                      {user.name}
                    </button>
                  )}
                  {showReadStatus && readStatus && (
                    <Badge variant={hasRead ? 'outline' : 'secondary'} className="gap-1 text-xs">
                      {hasRead ? (
                        <>
                          <Eye className="h-3 w-3" />
                          Read
                        </>
                      ) : (
                        <>
                          <Mail className="h-3 w-3" />
                          Unread
                        </>
                      )}
                    </Badge>
                  )}
                </div>
              )
            })}

          {hiddenCount > 0 && (
            <div
              className={`flex items-center justify-center h-${avatarSize === 'sm' ? '6' : avatarSize === 'md' ? '8' : '10'} w-${avatarSize === 'sm' ? '6' : avatarSize === 'md' ? '8' : '10'} rounded-full bg-muted border-2 border-background text-xs font-medium`}
            >
              +{hiddenCount}
            </div>
          )}
        </div>

        {showViewAll && users.length > 0 && (
          <div className="flex items-center gap-1 mt-1 sm:mt-0">
            {viewAllUrl ? (
              <Button
                variant="ghost"
                size="sm"
                className="h-7 text-xs"
                onClick={() => navigate(viewAllUrl)}
              >
                {viewAllLabel}
              </Button>
            ) : (
              users.length > maxVisible && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0">
                      <Plus className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>View all {users.length} users</p>
                  </TooltipContent>
                </Tooltip>
              )
            )}
          </div>
        )}
      </div>
    </TooltipProvider>
  )
}
